import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { StudentStudentIdSchema } from '../students/schemas';
import { ClassSchema, DBClassSchema, DBClassStudentsEntrySchema } from './schemas';

type Class = z.infer<typeof ClassSchema>;
type DBClass = z.infer<typeof DBClassSchema>;

export const ClassCodecConfig: TransformerConfig<typeof ClassSchema, typeof DBClassSchema> = {
  name: 'Class',
  clientSchema: ClassSchema,
  dbSchema: DBClassSchema,
  transformers: [
    // students: [{ studentId, name, schoolId, schoolLabel, slackId }] ↔
    //   students: { studentId: { name, schoolId, schoolLabel, slackId } }
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['students'] as const,
        encode: ({ students }) => ({
          students: (students as Class['students']).reduce(
            (
              acc: Record<
                z.infer<typeof StudentStudentIdSchema>,
                z.infer<typeof DBClassStudentsEntrySchema>
              >,
              student: Class['students'][number]
            ) => {
              acc[student.studentId] = {
                name: student.name,
                ...(student.schoolId ? { schoolId: student.schoolId } : {}),
                ...(student.schoolLabel ? { schoolLabel: student.schoolLabel } : {}),
                slackId: student.slackId,
              };
              return acc;
            },
            {} as Record<
              z.infer<typeof StudentStudentIdSchema>,
              z.infer<typeof DBClassStudentsEntrySchema>
            >
          ),
        }),
      },
      decoder: {
        sourceFields: ['students'] as const,
        decode: ({ students }) => ({
          students: Object.entries((students ?? {}) as NonNullable<DBClass['students']>).map(
            ([studentId, student]: [string, NonNullable<DBClass['students']>[string]]) => {
              return {
                studentId,
                ...student,
              };
            }
          ),
        }),
      },
    }),

    // activeDayTimeSortKey: computed from active, day, time
    // Uses dependencies for partial updates where only some fields are provided
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['active', 'day', 'time'] as const,
        dependencies: ['active', 'day', 'time'] as const,
        encode: ({ active, day, time }) => ({
          activeDayTimeSortKey: `${active}#${day}#${time}`,
          active,
          day,
          time,
        }),
      },
      decoder: {
        sourceFields: ['activeDayTimeSortKey'] as const,
        decode: () => ({}),
      },
    }),

    // activeStreamSortKey: computed from active, course, stream
    // Uses dependencies for partial updates where only some fields are provided
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['active', 'course', 'stream'] as const,
        dependencies: ['active', 'course', 'stream'] as const,
        encode: ({ active, course, stream }) => ({
          activeStreamSortKey: `${active}#${course}#${stream}`,
          active,
          course,
          stream,
        }),
      },
      decoder: {
        sourceFields: ['activeStreamSortKey'] as const,
        decode: () => ({}),
      },
    }),

    // slackChannelName: computed from course and classId
    // Uses dependencies for partial updates where only some fields are provided
    createTransformer<typeof ClassSchema, typeof DBClassSchema>({
      encoder: {
        sourceFields: ['course', 'classId'] as const,
        dependencies: ['course', 'classId'] as const,
        encode: ({ course, classId }) => ({
          slackChannelName: `cl-${course}-${classId}`,
          course,
          classId,
        }),
      },
      decoder: {
        sourceFields: ['slackChannelName'] as const,
        decode: ({ slackChannelName }) => ({
          slackChannelName,
        }),
      },
    }),
  ],
};

export const encodeClassToDb = createEncodeToDbFn(ClassCodecConfig);
export const decodeClassFromDb = createDecodeFromDbFn(ClassCodecConfig);
